import { Button, IconButton, Stack, Typography } from '@mui/material';
import { createHouses, GET_SCHOOL_PROPERTIES_QUERY } from '@schooly/api';
import { ApiError } from '@schooly/api';
import { useNotifications } from '@schooly/components/notifications';
import { usePrevious } from '@schooly/hooks/use-previous';
import {
  CheckIcon,
  CrossIcon,
  DeleteIcon,
  DragIcon,
  PlusIcon,
  SimpleButton,
  Spin,
} from '@schooly/style';
import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useCallback, useState } from 'react';
import { DragDropContext, Draggable, Droppable, OnDragEndResponder } from 'react-beautiful-dnd';
import { SubmitHandler } from 'react-hook-form';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { DndListWrapper } from '../../../../../components/uikit/Dnd/dnd.styled';
import { ControlTextField } from '../../../../../components/uikit-components/FormTextField/ControlTextField';
import {
  ModalContent,
  ModalFooter,
  ModalMain,
  ModalSmall,
} from '../../../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeader } from '../../../../../components/uikit-components/Modal/ModalHeader';
import { useSchool } from '../../../../../hooks/useSchool';
import { SchoolSettingsLayout } from '../../../SchoolSettingsLayout';

export interface SchoolTuneHousesForm {
  houses?: { id?: string; name?: string }[];
}

const getEmptyHouse = () => ({
  id: undefined,
  name: undefined,
});

export const SchoolTuneHousesModal: FC = () => {
  const navigate = useNavigate();
  const { $t } = useIntl();
  const queryClient = useQueryClient();

  const { showError } = useNotifications();
  const { studentPropertiesMap, schoolId, currentSchool } = useSchool();
  const form = useForm<SchoolTuneHousesForm>({
    defaultValues: {
      houses: studentPropertiesMap.house?.length
        ? [...studentPropertiesMap.house]
        : [getEmptyHouse()],
    },
  });
  const { fields, append, remove, replace, move } = useFieldArray({
    control: form.control,
    name: 'houses',
  });

  const [saving, setSaving] = useState(false);

  const prevFields = usePrevious(fields);

  const firstField = form.getValues('houses.0');

  const shouldFocusLastItem = Boolean(
    fields.length === 1
      ? !firstField.name
      : prevFields && fields && fields.length - prevFields.length === 1,
  );

  const handleClose = useCallback(() => {
    if (saving) {
      return;
    }

    navigate({ pathname: '/settings/tune', search: '?tab=houses' });
  }, [navigate, saving]);

  const addHouse = useCallback(() => {
    append(getEmptyHouse());
  }, [append]);

  const deleteHouse = useCallback(
    (index: number) => () => {
      if (fields.length > 1) {
        remove(index);
      } else {
        replace([getEmptyHouse()]);
      }
    },
    [fields.length, remove, replace],
  );

  const handleDragEnd = useCallback<OnDragEndResponder>(
    (result) => {
      // dropped outside the list
      if (!result.destination) {
        return;
      }

      move(result.source.index, result.destination.index);
    },
    [move],
  );

  const handleSubmit = useCallback<SubmitHandler<SchoolTuneHousesForm>>(
    async (data) => {
      if (!schoolId || !data.houses) {
        return;
      }

      setSaving(true);

      try {
        await createHouses(
          schoolId,
          data.houses.map((house, index) => ({
            name: house.name ?? '',
            order: index + 1,
          })),
        );

        queryClient.invalidateQueries([GET_SCHOOL_PROPERTIES_QUERY]);

        navigate({ pathname: '/settings/tune', search: '?tab=houses' });
      } catch (err) {
        console.error(err);
        showError(err as ApiError);
      }

      setSaving(false);
    },
    [navigate, queryClient, schoolId, showError],
  );

  return (
    <SchoolSettingsLayout>
      <ModalSmall open>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <ModalHeader title={currentSchool?.name} active>
              {!saving && (
                <IconButton onClick={handleClose}>
                  <CrossIcon />
                </IconButton>
              )}
            </ModalHeader>
            <ModalMain>
              <ModalContent active>
                <Stack gap={2.5} sx={{ height: '100%' }}>
                  <Typography variant="h2">
                    <FormattedMessage id="school-tabs-Houses" />
                  </Typography>

                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="HousesDroppable">
                      {(provided) => (
                        <DndListWrapper
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          alignItems="flex-start"
                          gap={2}
                        >
                          {fields?.map((field, index) => (
                            <Draggable key={field.id} draggableId={field.id} index={index}>
                              {(provided) => (
                                <Stack
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  direction="row"
                                  alignItems="center"
                                  gap={1}
                                  sx={{
                                    width: '100%',
                                    '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' },
                                  }}
                                  style={provided.draggableProps.style}
                                >
                                  {fields.length > 1 && (
                                    <IconButton inverse {...provided.dragHandleProps}>
                                      <DragIcon />
                                    </IconButton>
                                  )}
                                  <ControlTextField
                                    name={`houses.${index}.name`}
                                    control={form.control}
                                    rules={{ required: true }}
                                    label={$t({ id: 'school-tabs-Houses-HouseName' })}
                                    autoFocus={shouldFocusLastItem && index === fields.length - 1}
                                    fullWidth
                                    canClear
                                  />
                                  {fields.length > 1 && (
                                    <IconButton inverse onClick={deleteHouse(index)}>
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </Stack>
                              )}
                            </Draggable>
                          ))}

                          {provided.placeholder}

                          <SimpleButton startIcon={<PlusIcon />} onClick={addHouse}>
                            <FormattedMessage id="school-tabs-Houses-AddHouse" />
                          </SimpleButton>
                        </DndListWrapper>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Stack>
              </ModalContent>
            </ModalMain>
            <ModalFooter sx={{ justifyContent: 'space-between' }} active>
              <Button variant="outlined" disabled={saving} onClick={handleClose}>
                <FormattedMessage id="action-Cancel" />
              </Button>
              <Button type="submit" disabled={saving} endIcon={saving ? <Spin /> : <CheckIcon />}>
                <FormattedMessage id="action-Save" />
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalSmall>
    </SchoolSettingsLayout>
  );
};
